import { IEvent, Page, Post, IJobs, INews, ICaseStudy, IEbook } from './types';
import dayjs from 'dayjs';
import { BLACKLIST_BUILD_PATHS } from '@/lib/configuration';
import { Clients } from '@/components/types';

export async function getAllPages(locale: string) {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/pages?per_page=100`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET',
    },
  );

  if (!response.ok)
    throw new Error(
      `Error fetching pages: ${response.status} ${response.statusText}`,
    );
  const pages: Page[] = await response.json();
  return pages.filter(
    page =>
      !BLACKLIST_BUILD_PATHS.includes(page.slug) && page.acf.locale === locale,
  );
}

export async function getPageBySlug(
  locale: string,
  slugPath: string,
): Promise<Page | null> {
  try {
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/pages?slug=${slugPath}`,
      {
        headers,
        method: 'GET',
      },
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    const page = data[0];

    if (!page || page.acf.locale !== locale) {
      return null;
    }

    return page;
  } catch (err) {
    console.error(err);
    return null;
  }
}

export async function getAllPosts(type: string = 'posts', params: string = '') {
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/${type}?per_page=100&orderby=date&order=desc&${params}`,
    );
    if (!res.ok) {
      console.error(`Failed to fetch posts: ${res.statusText}`);
      return [];
    }
    const posts: (Post | IEvent | IJobs | INews | ICaseStudy | IEbook)[] =
      await res.json();
    return posts;
  } catch (error) {
    console.error(`Failed to fetch ${type}:`, error);
    return [];
  }
}
type PostType =
  | 'posts'
  | 'event'
  | 'jobs'
  | 'news'
  | 'case-study'
  | 'ebooks'
  | 'clients';
export async function getPostBySlug<T extends PostType>(
  locale: string,
  slug: string,
  type: T,
): Promise<
  T extends 'posts'
    ? Post | null
    : IEvent | IJobs | INews | ICaseStudy | IEbook | Clients | null
> {
  const url = `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/${type}?slug=${encodeURIComponent(slug)}`;
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
    method: 'GET',
  });

  if (!response.ok) {
    console.error('RESPONSE NOT OK: ', url);
    return null;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const bodySnippet = (await response.text()).slice(0, 300);
    console.error(
      `WP returned non-JSON (${contentType}) for ${url}. Body starts with: ${JSON.stringify(bodySnippet)}`,
    );
    return null;
  }
  try {
    const posts = await response.json();

    const post = posts.filter(
      (post: IEvent | Post | IJobs | INews | ICaseStudy | IEbook) =>
        post.acf?.locale === locale,
    )?.[0];
    if (!post) {
      return null;
    }
    return post;
  } catch (err) {
    console.error(`Failed to fetch ${url}:`, err);
    return null;
  }
}

export async function getPostsPage(
  locale: string,
  page: number,
  perPage: number,
  type: 'posts' | 'event' | 'jobs' | 'news' = 'posts',
) {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/${type}?page=${page}&per_page=${perPage}`,
  );
  if (!res.ok) {
    return [];
  }
  const posts: (Post | IEvent | IJobs | INews | ICaseStudy | IEbook)[] =
    await res.json();

  return (
    posts?.filter(
      post =>
        post.acf?.locale === locale || (!post.acf?.locale && locale === 'en'),
    ) || []
  );
}

/**
 * Filter events by locale and display flag
 */
function filterEventsByLocale(events: IEvent[], locale: string): IEvent[] {
  return events
    .filter(event => {
      // Check locale (default to 'en' if not set, matching calendar API behavior)
      const eventLocale = event.acf?.locale || 'en';
      if (eventLocale !== locale) return false;

      // Check display flag (truthy check, matching calendar API behavior)
      if (!event.acf?.display) return false;

      // Must have a start date
      if (!event.acf?.event_date_start) return false;

      return true;
    })
    .sort((eventA, eventB) => {
      const dateA = dayjs(eventA.acf?.event_date_start || '');
      const dateB = dayjs(eventB.acf?.event_date_start || '');
      return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
    });
}

export async function getAllEvents(
  locale: string,
  page: number,
  perPage: number,
) {
  // Fetch all events and filter client-side since WordPress REST API doesn't support complex meta queries
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/event?per_page=100&orderby=date&order=desc&_embed`,
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch events: ${res.statusText}`);
  }
  const allPosts: IEvent[] = await res.json();

  // Filter and sort events by locale only
  const events = filterEventsByLocale(allPosts, locale);

  // Apply pagination
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  return events.slice(startIndex, endIndex);
}

export async function getTotalPosts(
  locale: string,
  type:
    | 'posts'
    | 'event'
    | 'jobs'
    | 'news'
    | 'case-study'
    | 'ebooks'
    | 'clients' = 'posts',
) {
  const posts = await getAllPosts(type);
  return (
    posts?.filter(
      post =>
        post.acf?.locale === locale || (!post.acf?.locale && locale === 'en'),
    ).length || 0
  );
}

export async function getTotalEvents(locale: string) {
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/event?per_page=100&orderby=date&order=desc&_embed`,
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch events: ${res.statusText}`);
  }
  const allPosts: IEvent[] = await res.json();

  // Filter events and return count
  const events = filterEventsByLocale(allPosts, locale);
  return events.length;
}
